import { EditActions, RPCAction } from 'lib/actions/rpc'
import { CancelFunc, emptyCancel } from 'lib/cancel'
import { Status } from 'lib/rpc'
import { ActionStatus, expandValue, unaryInvoke } from 'lib/rpcts'
import { App } from 'pbts/c1/api/app/v1/app'
import {
    AppResource,
    AppResourceExpandMask,
    AppResourceServiceGetRequest,
    AppResourceServiceGetResponse,
    AppResourceServiceUpdateRequest,
    AppResourceServiceUpdateResponse,
    AppResourceType,
    AppResourceView,
} from 'pbts/c1/api/app/v1/app_resource'
import { AppResourceServiceClient } from 'pbts/c1/api/app/v1/app_resource.client'
import { ActorObjectPermissions } from 'pbts/c1/api/iam/v1/authorization'
import { Any } from 'pbts/google/protobuf/any'
import { Dispatch } from 'react'

export enum AppResourceAction {
    get = 'AppResourcesAction.get',
    update = 'AppResourcesAction.update',
}

export type GetAction = {
    type: AppResourceAction.get
    payload: ActionStatus<AppResourceValue>
}

export type UpdateAction = {
    type: AppResourceAction.update
    payload: ActionStatus<AppResourceValue>
}

export type AppResourceActions = GetAction | UpdateAction
export const expandAppResourcePayload = (
    payload: ActionStatus<AppResourceServiceGetResponse> | ActionStatus<AppResourceServiceUpdateResponse>,
): ActionStatus<AppResourceValue> => {
    switch (payload.status) {
        case Status.OK:
            return {
                status: Status.OK,
                item: expandAppResourceView(payload.item.appResourceView, payload.item.expanded),
            }
        default:
            return payload
    }
}

export interface AppResourceValue extends AppResource {
    app?: App
    appResourceType?: AppResourceType
    parentAppResourceType?: AppResourceType
    parentAppResource?: AppResource
    objectPermissions?: ActorObjectPermissions
}

export const expandAppResourceView = (msg: AppResourceView | undefined, values: Any[]): AppResourceValue => {
    return {
        ...AppResource.create(msg?.appResource ?? {}),
        app: expandValue<App>(msg?.appPath, App, values),
        appResourceType: expandValue<AppResourceType>(msg?.resourceTypePath, AppResourceType, values),
        parentAppResourceType: expandValue<AppResourceType>(msg?.parentResourceTypePath, AppResourceType, values),
        parentAppResource: expandValue<AppResource>(msg?.parentResourcePath, AppResource, values),
        objectPermissions: msg?.objectPermissions,
    }
}

export const getAppResource = (
    dispatch: Dispatch<AppResourceActions>,
    appResourceId: string,
    appId: string,
    appResourceTypeId: string,
    expandMask?: AppResourceExpandMask,
    client?: AppResourceServiceClient,
): CancelFunc => {
    if (!client) {
        return emptyCancel
    }
    const req = AppResourceServiceGetRequest.create({
        appId,
        appResourceTypeId,
        id: appResourceId,
        expandMask,
    })

    return unaryInvoke<AppResourceServiceGetRequest, AppResourceServiceGetResponse>(
        req,
        client.get.bind(client),
        (payload) => {
            dispatch({
                type: AppResourceAction.get,
                payload: expandAppResourcePayload(payload),
            })
        },
    )
}

export const updateAppResource = (
    dispatch: Dispatch<AppResourceActions>,
    dispatchField: Dispatch<EditActions>,
    setAppResourceRequest: AppResourceServiceUpdateRequest,
    client?: AppResourceServiceClient,
): CancelFunc => {
    if (!client) {
        return emptyCancel
    }

    dispatchField({
        type: RPCAction.edit,
        payload: {
            status: Status.LOADING,
        },
    })

    return unaryInvoke<AppResourceServiceUpdateRequest, AppResourceServiceUpdateResponse>(
        setAppResourceRequest,
        client.update.bind(client),
        (payload) => {
            dispatch({
                type: AppResourceAction.update,
                payload: expandAppResourcePayload(payload),
            })
            dispatchField({
                type: RPCAction.edit,
                payload,
            })
        },
    )
}

export const updateAppResourceField = <T>(
    dispatch: Dispatch<UpdateAction>,
    dispatchField: Dispatch<EditActions>,
    client: AppResourceServiceClient | undefined,
    makeRequest: (value: T) => AppResourceServiceUpdateRequest | undefined,
): ((value: T) => void) => {
    const doEdit = async (value: T): Promise<void> => {
        if (!client) {
            return
        }
        dispatchField({
            type: RPCAction.edit,
            payload: {
                status: Status.LOADING,
            },
        })

        const req = makeRequest(value)
        if (req === undefined) {
            return
        }
        req.expandMask = { paths: ['*'] }

        unaryInvoke<AppResourceServiceUpdateRequest, AppResourceServiceUpdateResponse>(
            req,
            client.update.bind(client),
            (payload) => {
                if (payload.status === Status.OK) {
                    dispatch({
                        type: AppResourceAction.update,
                        payload: expandAppResourcePayload(payload),
                    })
                    dispatchField({
                        type: RPCAction.edit,
                        payload,
                    })
                } else {
                    dispatch({
                        type: AppResourceAction.update,
                        payload,
                    })
                }
            },
        )
    }

    return (value: T): void => {
        doEdit(value)
    }
}
